import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import termsAndConditionsService from '@/api/termsAndConditionsService';
import { TermsAndConditionContent } from '@/interfaces/termsAndConditions';

export interface JWTTermsAndConditionsClaims {
  acceptedTermsId: string | null;
  latestTerms: {
    id: string;
    effectiveDate: string;
  } | null;
}

interface TermsAndConditionsState {
  termsId: string | null;
  latestVersion: string;
  userAcceptedTermsId: string | null;
  termsContents: TermsAndConditionContent[];
  publishedAt: string;
  effectiveDate: string;
  showModal: boolean;
  isMandatory: boolean;
  modalDismissed: boolean;
  loading: boolean;
  error: string | null;
  jwtLatestTermsId: string | null;
  jwtEffectiveDate: string | null;
}

const initialState: TermsAndConditionsState = {
  termsId: null,
  latestVersion: '',
  userAcceptedTermsId: null,
  termsContents: [],
  publishedAt: '',
  effectiveDate: '',
  showModal: false,
  isMandatory: false,
  modalDismissed: false,
  loading: false,
  error: null,
  jwtLatestTermsId: null,
  jwtEffectiveDate: null,
};

const isEffectiveDateActive = (effectiveDate: string | null): boolean => {
  if (!effectiveDate) return false;
  const effectiveTimestamp = Date.parse(effectiveDate);
  return Number.isFinite(effectiveTimestamp) && effectiveTimestamp <= Date.now();
};

const shouldShowPopup = (acceptedTermsId: string | null, latestTermsId: string | null): boolean =>
  Boolean(latestTermsId && acceptedTermsId !== latestTermsId);

export const hasMandatoryUnacceptedTerms = (claims: JWTTermsAndConditionsClaims): boolean =>
  Boolean(
    claims.latestTerms?.id && claims.acceptedTermsId !== claims.latestTerms.id && isEffectiveDateActive(claims.latestTerms.effectiveDate)
  );

export const fetchLatestTermsAndConditions = createAsyncThunk('termsAndConditions/fetchLatest', async (_, { rejectWithValue }) => {
  try {
    const result = await termsAndConditionsService.getPublishedTerms();
    return result;
  } catch (error: any) {
    return rejectWithValue(error?.data?.message || 'Failed to fetch terms and conditions');
  }
});

export const acceptTermsAndConditions = createAsyncThunk('termsAndConditions/accept', async (termsId: string, { rejectWithValue }) => {
  try {
    await termsAndConditionsService.acceptTerms(termsId);
    return { termsId, acceptedAt: new Date().toISOString() };
  } catch (error: any) {
    return rejectWithValue(error?.data?.message || 'Failed to accept terms and conditions');
  }
});

export const termsAndConditionsSlice = createSlice({
  name: 'termsAndConditions',
  initialState,
  reducers: {
    setShowTermsAndConditionsModal: (state, action: PayloadAction<boolean>) => {
      state.showModal = action.payload;
    },

    checkTermsAndConditionsFromJWT: (state, action: PayloadAction<JWTTermsAndConditionsClaims>) => {
      const { acceptedTermsId, latestTerms } = action.payload;
      state.userAcceptedTermsId = acceptedTermsId;

      if (!latestTerms?.id) {
        state.termsId = null;
        state.latestVersion = '';
        state.termsContents = [];
        state.publishedAt = '';
        state.effectiveDate = '';
        state.jwtLatestTermsId = null;
        state.jwtEffectiveDate = null;
        state.showModal = false;
        state.isMandatory = false;
        state.modalDismissed = false;
        state.error = null;
        return;
      }

      if (state.jwtLatestTermsId !== latestTerms.id) {
        state.modalDismissed = false;
      }

      state.jwtLatestTermsId = latestTerms.id;
      state.jwtEffectiveDate = latestTerms.effectiveDate;
      state.termsId = latestTerms.id;
      state.isMandatory = isEffectiveDateActive(latestTerms.effectiveDate);
      state.showModal = shouldShowPopup(acceptedTermsId, latestTerms.id) && (!state.modalDismissed || state.isMandatory);
    },

    dismissTermsAndConditionsModal: state => {
      const latestId = state.jwtLatestTermsId || state.termsId;
      state.isMandatory = isEffectiveDateActive(state.jwtEffectiveDate || state.effectiveDate);
      if (latestId && !state.isMandatory) {
        state.modalDismissed = true;
        state.showModal = false;
      } else if (latestId) {
        state.showModal = shouldShowPopup(state.userAcceptedTermsId, latestId);
      }
    },

    recheckTermsAndConditions: state => {
      const latestId = state.jwtLatestTermsId || state.termsId;
      state.isMandatory = isEffectiveDateActive(state.jwtEffectiveDate || state.effectiveDate);
      state.showModal = shouldShowPopup(state.userAcceptedTermsId, latestId) && (!state.modalDismissed || state.isMandatory);
    },

    resetTermsAndConditionsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLatestTermsAndConditions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestTermsAndConditions.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (!payload?.id) return;

        state.latestVersion = payload.version;
        state.termsContents = payload.termsAndConditionContents || [];
        state.publishedAt = payload.publishedAt;
        state.effectiveDate = payload.effectiveDate;

        if (payload.id !== state.jwtLatestTermsId) {
          // The JWT claim is missing or stale: the published endpoint is the
          // source of truth for the latest version and its effective date.
          if (state.termsId !== payload.id) state.modalDismissed = false;
          state.termsId = payload.id;
          state.isMandatory = isEffectiveDateActive(payload.effectiveDate);
          state.showModal = shouldShowPopup(state.userAcceptedTermsId, payload.id) && (!state.modalDismissed || state.isMandatory);
        }
      })
      .addCase(fetchLatestTermsAndConditions.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch terms and conditions';
      })
      .addCase(acceptTermsAndConditions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptTermsAndConditions.fulfilled, (state, action) => {
        state.loading = false;
        state.userAcceptedTermsId = action.payload.termsId;
        state.showModal = false;
        state.modalDismissed = false;
        state.error = null;
      })
      .addCase(acceptTermsAndConditions.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to accept terms and conditions';
      });
  },
});

export const {
  setShowTermsAndConditionsModal,
  checkTermsAndConditionsFromJWT,
  dismissTermsAndConditionsModal,
  recheckTermsAndConditions,
  resetTermsAndConditionsState,
} = termsAndConditionsSlice.actions;

export const selectTermsAndConditions = (state: RootState) => state.termsAndConditions;

export const selectTermsContentByLanguage = (state: RootState, language: string): string => {
  const { termsContents } = state.termsAndConditions;
  const normalizedLanguage = language.toUpperCase();
  const content = termsContents.find(tc => tc.language.toUpperCase() === normalizedLanguage);
  if (content) {
    return content.content;
  }
  const englishContent = termsContents.find(tc => tc.language.toUpperCase() === 'EN');
  return englishContent?.content || '';
};

export default termsAndConditionsSlice.reducer;
